import { isUsingNewLayout } from '@app/adapters/silverfin'
export class LeftDrawerCacheServiceKlass {
  private readonly dataCache = new Map<string, unknown>()
  private readonly scrollPositionCache = new Map<string, number>()

  // ─── Generic Cache ─────────────────────────────────────────────────────────
  private getCached<T>(type: string, key: string): T | null {
    return (this.dataCache.get(this.createPrefix(type, key)) as T) ?? null
  }

  private setCached<T>(type: string, key: string, value: T): void {
    this.dataCache.set(this.createPrefix(type, key), value)
  }

  createPrefix(type: string, key: string): string {
    return `${type}:${key}`
  }

  deleteCacheByPredicate(predicate: (k: string) => boolean): void {
    for (const k of this.dataCache.keys()) {
      if (predicate(k)) this.dataCache.delete(k)
    }
  }

  // ─── Cache-or-Fetch ───────────────────────────────────────────────────────
  async getOrFetch<T>(
    type: string,
    key: string,
    fetcher: () => Promise<T | null>
  ): Promise<T | null> {
    if (isUsingNewLayout()) {
      const cached = this.getCached<T>(type, key)
      if (cached !== null) return cached
    }
    const result = await fetcher()
    if (isUsingNewLayout() && result !== null) {
      this.setCached(type, key, result)
    }
    return result
  }

  // ─── Overview Cache Key ────────────────────────────────────────────────────
  buildOverviewCacheKey(params: {
    filter: string | null
    includeReviewStatus: boolean
    ledgerId: number
    workflowId: number
    currentFirmId: string
    currentCompanyId: string
    locale: string
  }): string {
    // We need the prefix to be able to clear all overview cache entries for a given workflow when we know
    // they are stale, but the rest of the params are needed to distinguish between different overview states
    // (e.g. with different filters applied)
    const keyPrefix = this.buildCacheKey(params)
    const keySuffix = JSON.stringify({
      filter: params.filter,
      includeReviewStatus: params.includeReviewStatus,
    })

    return `${keyPrefix}:${keySuffix}`
  }

  buildCacheKey(params: {
    currentFirmId: string
    currentCompanyId: string
    ledgerId?: number
    workflowId?: number
    locale?: string
  }): string {
    const keyParts = [
      params.currentFirmId,
      params.currentCompanyId,
      params.ledgerId ?? undefined,
      params.workflowId ?? undefined,
      params.locale ?? undefined,
    ]
      .filter((part) => part !== undefined)
      .join(':')

    return keyParts
  }

  // ─── Scroll Position Cache ─────────────────────────────────────────────────
  getScrollPosition(key: string): number | null {
    return this.scrollPositionCache.get(key) ?? null
  }

  setScrollPosition(key: string, top: number): void {
    this.scrollPositionCache.set(key, top)
  }

  // ─── Teardown ──────────────────────────────────────────────────────────────
  teardown(): void {
    this.dataCache.clear()
    this.scrollPositionCache.clear()
  }
}

export const LeftDrawerCacheService = new LeftDrawerCacheServiceKlass()
